import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import type { ApprovalComment } from '@/lib/api/types'
import { Send, Loader2, MessageSquare, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAvatarColor } from '@/lib/avatar-colors'

interface ApprovalCommentsListProps {
  comments: ApprovalComment[]
  onAddComment: (content: string) => Promise<void>
  onResolveComment: (commentId: string) => Promise<void>
  className?: string
}

export function ApprovalCommentsList({
  comments,
  onAddComment,
  onResolveComment,
  className,
}: ApprovalCommentsListProps) {
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resolvingId, setResolvingId] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!newComment.trim()) return

    setSubmitting(true)
    try {
      await onAddComment(newComment.trim())
      setNewComment('')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResolve = async (commentId: string) => {
    setResolvingId(commentId)
    try {
      await onResolveComment(commentId)
    } finally {
      setResolvingId(null)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  const unresolvedComments = sortedComments.filter((c) => !c.isResolved)
  const resolvedComments = sortedComments.filter((c) => c.isResolved)

  return (
    <div className={cn('space-y-4', className)}>
      {/* Add Comment Form */}
      <div className="space-y-2">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          rows={3}
          className="resize-none"
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !newComment.trim()}
            size="sm"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Post Comment
          </Button>
        </div>
      </div>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
          <MessageSquare className="h-8 w-8 text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No comments yet</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            Be the first to add a comment
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Unresolved Comments */}
          {unresolvedComments.length > 0 && (
            <div className="space-y-3">
              {unresolvedComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onResolve={() => handleResolve(comment.id)}
                  resolving={resolvingId === comment.id}
                  getInitials={getInitials}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}

          {/* Resolved Comments */}
          {resolvedComments.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>{resolvedComments.length} resolved comment{resolvedComments.length !== 1 ? 's' : ''}</span>
              </div>
              {resolvedComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onResolve={() => handleResolve(comment.id)}
                  resolving={resolvingId === comment.id}
                  getInitials={getInitials}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface CommentItemProps {
  comment: ApprovalComment
  onResolve: () => void
  resolving: boolean
  getInitials: (name: string) => string
  formatDate: (dateString: string) => string
}

function CommentItem({
  comment,
  onResolve,
  resolving,
  getInitials,
  formatDate,
}: CommentItemProps) {
  return (
    <div
      className={cn(
        'flex gap-3 p-3 rounded-lg transition-colors',
        comment.isResolved
          ? 'bg-neutral-50 dark:bg-neutral-800/50 opacity-75'
          : 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700'
      )}
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className={`text-xs ${getAvatarColor(comment.authorId || comment.authorName).bg} ${getAvatarColor(comment.authorId || comment.authorName).text}`}>
          {getInitials(comment.authorName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
            {comment.authorName}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {formatDate(comment.createdAt)}
          </span>
          {comment.isResolved && (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Resolved
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-1 whitespace-pre-wrap">
          {comment.content}
        </p>

        {/* Resolve Action */}
        {!comment.isResolved && (
          <div className="flex items-center gap-2 mt-2">
            <Checkbox
              id={`resolve-${comment.id}`}
              checked={false}
              onCheckedChange={onResolve}
              disabled={resolving}
            />
            <label
              htmlFor={`resolve-${comment.id}`}
              className="text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer"
            >
              {resolving ? 'Resolving...' : 'Mark as resolved'}
            </label>
          </div>
        )}
      </div>
    </div>
  )
}
